// Mirrors Event::Page#display_hash. The rendered `html` fields come from
// MarkdownRenderer server-side - never render `body`/`answer` as markup, they
// are the unsanitized source the editor writes back.

export interface HeroBlock {
  type: "hero";
  heading: string | null;
  subheading: string | null;
}

export interface TextBlock {
  type: "text";
  body: string;
  html?: string;
}

export interface ImageBlock {
  type: "image";
  url: string;
  caption: string | null;
}

// The wall of photos the city page opens with. `url` is either an https
// address the organizer pasted or one of our own Active Storage paths.
export interface GalleryBlock {
  type: "gallery";
  heading: string | null;
  items: { url: string; caption: string | null }[];
}

export interface ScheduleBlock {
  type: "schedule";
  heading: string | null;
  items: { time: string | null; label: string }[];
}

export interface FaqBlock {
  type: "faq";
  heading: string | null;
  items: { question: string; answer: string; answer_html?: string }[];
}

export interface LinksBlock {
  type: "links";
  items: { label: string; url: string }[];
}

// The logo and the link are both optional: a boba shop that donated the drinks
// often has neither, and the name alone is still a credit worth printing.
export interface SponsorsBlock {
  type: "sponsors";
  heading: string | null;
  items: { name: string; url: string | null; logo_url: string | null }[];
}

export type EventPageBlock =
  | HeroBlock
  | TextBlock
  | ImageBlock
  | GalleryBlock
  | ScheduleBlock
  | FaqBlock
  | LinksBlock
  | SponsorsBlock;

export type EventPageBlockType = EventPageBlock["type"];

export interface EventPage {
  blocks: EventPageBlock[];
  // Null unless the organizer wrote some and HQ has the escape hatch turned on.
  // Only ever rendered inside a sandboxed iframe.
  custom_html: string | null;
  // The frame can't size itself without scripts, so the height is the
  // organizer's choice, already clamped server-side.
  custom_html_height: number;
  custom_html_enabled: boolean;
}

export const BLOCK_LABELS: Record<EventPageBlockType, string> = {
  hero: "Headline",
  text: "Text",
  image: "Photo",
  gallery: "Photo wall",
  schedule: "Schedule",
  faq: "FAQ",
  links: "Buttons",
  sponsors: "Sponsors",
};

// Where each section lands on the public city page. The page is a designed
// layout rather than a stack, so a block goes to the slot it was written for
// and only the leftovers run in order down the middle.
export const BLOCK_SLOTS: Record<EventPageBlockType, string> = {
  gallery: "The photos beside your headline, up at the top.",
  text: "The \u201cWhat is a Boba Bash?\u201d bubble.",
  image: "Beside your event details, captioned as the venue.",
  faq: "The FAQ at the bottom, in place of the standard questions.",
  sponsors: "The thank-you panel near the bottom.",
  hero: "Down the middle of the page, under your event details.",
  schedule: "Down the middle of the page, under your event details.",
  links: "Down the middle of the page, under your event details.",
};

export const BLOCK_TYPES = Object.keys(BLOCK_LABELS) as EventPageBlockType[];

// One row of HQ's custom-code queue. Deliberately carries no markup - the
// admin list only needs to know a page has some, and how much.
export interface CustomPageRow {
  id: number;
  name: string;
  location_label: string;
  organizer_name: string | null;
  page_published: boolean;
  bytes: number;
}
